/**
 * KPI Card component for the KPI Overview grid.
 * Extracted from KPIOverviewTab.tsx for better code organization.
 */

import React, { memo } from 'react';
import { Linking, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';
import { formatKpiValueByName, formatKpiTargetByName } from '../utils/formatters';
import {
	KpiMetric,
	LinkedReportInfo,
	getDeltaColor,
	getDisplayDelta,
	getDeltaArrowIcon,
	getCategoryIcon,
	getCategoryContentColor,
	getCategoryBackgroundColor,
	resolveKpiCategory,
	PLATFORM_BADGE_STYLES,
	KPI_CATEGORY_COLORS_DARK,
	PLATFORM_BADGE_STYLES_DARK,
} from '../utils/kpiHelpers';

interface KPICardProps {
	metric: KpiMetric;
	onOpen: (metric: KpiMetric) => void;
	searchTerm?: string;
	linkedReport?: LinkedReportInfo | null;
	/**
	 * When provided, clicking the platform badge (Power BI / Tableau) will invoke this
	 * callback with the KPI name instead of opening the external link directly.
	 * Used by the KPI Overview tab to deep-link into the Reports tab with a pre-applied filter.
	 */
	onNavigateToReports?: (kpiName: string) => void;
}

const KPICard: React.FC<KPICardProps> = memo(({ metric, onOpen, searchTerm = '', linkedReport, onNavigateToReports }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';

	// Resolve category colours — use dark palette when in dark mode
	const resolvedCategory = resolveKpiCategory(metric.category);
	const darkCategoryColors = resolvedCategory ? KPI_CATEGORY_COLORS_DARK[resolvedCategory] : null;
	const categoryBadgeBg = isDark && darkCategoryColors
		? darkCategoryColors.background
		: getCategoryBackgroundColor(metric.category);
	const categoryContentColor = isDark && darkCategoryColors
		? darkCategoryColors.icon
		: getCategoryContentColor(metric.category);

	const categoryFullName = resolvedCategory ?? metric.category;

	const deltaColor = getDeltaColor(metric.isUnfavorable);

	// In dark mode, brighten the delta colours slightly for contrast
	const effectiveDeltaColor = isDark
		? metric.isUnfavorable ? '#FF8A80' : '#6FD98A'
		: deltaColor;
	const deltaIcon = getDeltaArrowIcon(metric.actual, metric.target);

	const platformLabel = linkedReport?.platform || '';
	const platformPalette = isDark
		? (PLATFORM_BADGE_STYLES_DARK[platformLabel] ?? PLATFORM_BADGE_STYLES_DARK['TBD'])
		: (PLATFORM_BADGE_STYLES[platformLabel] ?? PLATFORM_BADGE_STYLES['TBD']);
	const hasLinkedReport = Boolean(linkedReport && linkedReport.reportLink && linkedReport.reportLink !== 'TBD');

	const handleCardPress = () => { onOpen(metric); };

	const handleReportBadgePress = () => {
		if (onNavigateToReports) {
			onNavigateToReports(metric.name);
			return;
		}
		if (hasLinkedReport && linkedReport?.reportLink) {
			if (Platform.OS === 'web') {
				const webWindow = globalThis as typeof globalThis & {
					open?: (url?: string, target?: string, features?: string) => Window | null;
				};
				webWindow.open?.(linkedReport.reportLink, '_blank', 'noopener,noreferrer');
			} else {
				Linking.openURL(linkedReport.reportLink).catch(() => {});
			}
		}
	};

	const renderHighlightedText = (value: string, baseStyle: any) => {
		if (!searchTerm.trim()) {
			return <Text style={baseStyle} numberOfLines={1}>{value}</Text>;
		}
		const escapedQuery = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(`(${escapedQuery})`, 'ig');
		const parts = value.split(regex);
		return (
			<Text style={baseStyle} numberOfLines={1}>
				{parts.map((part, index) =>
					part.toLowerCase() === searchTerm.toLowerCase() ? (
						<Text key={`${value}-${index}`} style={[styles.kpiHighlightText, isDark && { backgroundColor: '#5C4800' }]}>{part}</Text>
					) : (
						<Text key={`${value}-${index}`}>{part}</Text>
					)
				)}
			</Text>
		);
	};

	const cardContent = (
		<View style={[styles.card, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
			{/* Top section: icon + KPI name left-aligned */}
			<View style={styles.cardTopRow}>
				<View style={[styles.cardIconWrap, { backgroundColor: categoryBadgeBg }]}>
					<MaterialIcons name={getCategoryIcon(metric.category)} size={20} color={categoryContentColor} />
				</View>
				<View style={styles.cardTitleRow}>
					{renderHighlightedText(metric.name, [styles.cardTitle, isDark && { color: colors.text.primary }])}
					{metric.name.trim().toUpperCase() === 'GAP TIME' && (
						<Text style={[styles.cardTitleSubText, isDark && { color: colors.text.disabled }]}>(Min / Week)</Text>
					)}
				</View>
			</View>

			{/* Metrics row */}
			<View style={styles.cardMetricsHeader}>
				<Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>ACTUAL</Text>
				<Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>TARGET</Text>
				<Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>DELTA</Text>
			</View>

			<View style={styles.cardMetricsValues}>
				<Text style={[styles.actualValue, isDark && { color: colors.text.primary }]}>
					{formatKpiValueByName(metric.actual, metric.format, metric.name)}
				</Text>
				<Text style={[styles.targetValue, isDark && { color: colors.text.secondary }]}>
					{formatKpiTargetByName(metric.target, metric.format, metric.name)}
				</Text>
				<View style={styles.deltaValueRow}>
					{metric.delta !== 0 && (
						<MaterialIcons name={deltaIcon} size={20} color={effectiveDeltaColor} />
					)}
					<Text style={[styles.deltaValue, { color: effectiveDeltaColor }]}>
						{formatKpiValueByName(getDisplayDelta(metric.delta), metric.format, metric.name)}
					</Text>
				</View>
			</View>

			{/* Bottom section: category badge left, report type badge right */}
			<View style={[styles.cardBottomRow, isDark && { borderTopColor: colors.border.divider }]}>
				<View style={[styles.categoryBadge, { backgroundColor: categoryBadgeBg }]}>
					{renderHighlightedText(categoryFullName, [styles.categoryBadgeText, { color: isDark && darkCategoryColors ? darkCategoryColors.text : categoryContentColor }])}
				</View>

				{linkedReport && platformLabel ? (
					<TouchableOpacity
						style={styles.reportBadgeRow}
						onPress={handleReportBadgePress}
						activeOpacity={0.7}
						accessibilityRole="link"
						accessibilityLabel="Open report"
					>
						<View style={[styles.reportTypeBadge, { backgroundColor: platformPalette.background }]}>
							<Text style={[styles.reportTypeBadgeText, { color: platformPalette.text }]}>Report(s)</Text>
						</View>
						<MaterialIcons name="chevron-right" size={20} color={isDark ? colors.text.secondary : '#A9B0BC'} />
					</TouchableOpacity>
				) : null}
			</View>
		</View>
	);

	return (
		<TouchableOpacity
			activeOpacity={hasLinkedReport ? 0.7 : 0.9}
			onPress={handleCardPress}
			accessibilityRole="button"
			accessibilityLabel={hasLinkedReport ? `Open ${metric.name} report in ${platformLabel}` : `View ${metric.name} details`}
		>
			{cardContent}
		</TouchableOpacity>
	);
});

const styles = StyleSheet.create({
	card: {
		backgroundColor: '#FFFFFF',
		borderRadius: 12,
		padding: 16,
		borderWidth: 1,
		borderColor: '#E3E9F3',
		shadowColor: '#A8B4C5',
		shadowOpacity: 0.12,
		shadowRadius: 8,
		shadowOffset: { width: 0, height: 2 },
		elevation: 2,
	},
	cardTopRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: 12,
		gap: 12,
	},
	cardIconWrap: {
		width: 36,
		height: 36,
		borderRadius: 8,
		alignItems: 'center',
		justifyContent: 'center',
	},
	cardTitleRow: {
		flexDirection: 'row',
		alignItems: 'baseline',
		flex: 1,
		flexShrink: 1,
		gap: 4,
	},
	cardTitle: {
		fontSize: 15,
		fontFamily: FontFamily.bold,
		color: '#2D2F33',
		lineHeight: 20,
		textAlign: 'left',
	},
	cardTitleSubText: {
		fontSize: 11,
		fontFamily: FontFamily.medium,
		color: '#7A8088',
		lineHeight: 16,
	},
	cardMetricsHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	metricLabel: {
		fontSize: 11,
		fontFamily: FontFamily.bold,
		color: '#7A8088',
		width: '32%',
		lineHeight: 14,
		textAlign: 'center',
	},
	cardMetricsValues: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: 6,
	},
	actualValue: {
		fontSize: 18,
		fontFamily: FontFamily.bold,
		color: '#2D2F33',
		width: '32%',
		lineHeight: 24,
		textAlign: 'center',
	},
	targetValue: {
		fontSize: 16,
		fontFamily: FontFamily.medium,
		color: '#6B6E72',
		width: '32%',
		lineHeight: 22,
		textAlign: 'center',
	},
	deltaValueRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32%',
	},
	deltaValue: {
		fontSize: 16,
		fontFamily: FontFamily.bold,
		lineHeight: 22,
	},
	cardBottomRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginTop: 14,
		paddingTop: 12,
		borderTopWidth: 1,
		borderTopColor: '#E3E9F3',
	},
	categoryBadge: {
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 12,
		alignItems: 'center',
		justifyContent: 'center',
	},
	categoryBadgeText: {
		fontSize: 11,
		fontFamily: FontFamily.bold,
		fontWeight: '700',
		color: '#1B5E20',
	},
	reportBadgeRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
	},
	reportTypeBadge: {
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 12,
		alignItems: 'center',
	},
	reportTypeBadgeText: {
		fontSize: 11,
		fontFamily: FontFamily.bold,
		lineHeight: 16,
	},
	kpiHighlightText: {
		backgroundColor: '#FFEAA6',
		borderRadius: 3,
	},
});

export default KPICard;
